import { useMemo, useCallback } from 'react';
import { Logger } from '@/core/utils/logger';
const logger = new Logger('messages');
import { useGetGuardrailsQuery, MessageGuardrail, GuardrailMatch, ScanResult } from '@/api/features/guardrails';

interface CompiledGuardrail {
    guardrail: MessageGuardrail;
    pattern: RegExp;
}

interface UseGuardrailScannerOptions {
    /** Whether guardrails scanning is enabled. Default true. */
    enabled?: boolean;
}

export function useGuardrailScanner(options: UseGuardrailScannerOptions = {}) {
    const { enabled = true } = options;

    // Skip the query entirely if guardrails are disabled
    const { data: guardrails, isLoading, error } = useGetGuardrailsQuery(undefined, {
        skip: !enabled
    });

    // Compile regex patterns once and memoize
    const compiledGuardrails = useMemo<CompiledGuardrail[]>(() => {
        if (!guardrails || guardrails.length === 0) {
            return [];
        }

        return guardrails.map((guardrail) => {
            let pattern: RegExp;

            if (guardrail.isRegex) {
                try {
                    pattern = new RegExp(guardrail.phrase, 'gi');
                } catch (e) {
                    logger.error(`[GuardrailScanner] Invalid regex pattern: ${guardrail.phrase}`, e);
                    // Fallback to literal match
                    const escaped = guardrail.phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                    pattern = new RegExp(`\\b${escaped}\\b`, 'gi');
                }
            } else {
                // Escape special regex characters and use word boundaries
                const escaped = guardrail.phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                pattern = new RegExp(`\\b${escaped}\\b`, 'gi');
            }

            return {
                guardrail,
                pattern
            };
        });
    }, [guardrails]);

    // Scan text function
    const scanText = useCallback((text: string): ScanResult => {
        const result: ScanResult = {
            hasWarnings: false,
            matches: [],
            highCount: 0,
            mediumCount: 0,
            lowCount: 0
        };

        // Return empty result if guardrails are disabled
        if (!enabled || !text || text.trim().length === 0 || compiledGuardrails.length === 0) {
            return result;
        }

        const matchedIds = new Set<string>();

        for (const { guardrail, pattern } of compiledGuardrails) {
            // Skip if already matched this guardrail
            if (matchedIds.has(guardrail.id)) {
                continue;
            }

            // Use a fresh RegExp instance per scan to avoid mutating shared regex state.
            const safePattern = new RegExp(pattern.source, pattern.flags);
            const match = text.match(safePattern);

            if (match) {
                matchedIds.add(guardrail.id);

                const guardrailMatch: GuardrailMatch = {
                    id: guardrail.id,
                    phrase: guardrail.phrase,
                    riskCategory: guardrail.riskCategory,
                    warningReason: guardrail.warningReason,
                    severity: guardrail.severity,
                    matchedText: match[0]
                };

                result.matches.push(guardrailMatch);

                switch (guardrail.severity) {
                    case 'HIGH':
                        result.highCount++;
                        break;
                    case 'MEDIUM':
                        result.mediumCount++;
                        break;
                    case 'LOW':
                        result.lowCount++;
                        break;
                }
            }
        }

        result.hasWarnings = result.matches.length > 0;

        // Sort matches by severity (HIGH first, then MEDIUM, then LOW)
        const severityOrder: Record<string, number> = { HIGH: 0, MEDIUM: 1, LOW: 2 };
        result.matches.sort((a, b) => severityOrder[a.severity] - severityOrder[b.severity]);

        return result;
    }, [enabled, compiledGuardrails]);

    return {
        scanText,
        isLoading,
        error,
        guardrailsCount: guardrails?.length ?? 0,
        enabled
    };
}
